"use client";

import { c as _c } from "react/compiler-runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import type { RouterOutputs } from "@/app/_trpc/client";
import { api } from "@/app/_trpc/client";
import NotFoundPage from "@/components/layout/NotFoundPage";
import { FORUM_MIN_LEVEL, FORUM_THREAD_POSTS_PER_PAGE, forumLevelMessage } from "@/drizzle/constants";
import { CommentOnForum } from "@/layout/Comment";
import ContentBox from "@/layout/ContentBox";
import Loader from "@/layout/Loader";
import Pagination from "@/layout/Pagination";
import RichInput from "@/layout/RichInput";
import { forumThreadIntro } from "@/layout/seoTexts";
import { showMutationToast } from "@/libs/toast";
import { parseHtml } from "@/utils/parse";
import { useUserData } from "@/utils/UserContext";
import { type MutateCommentSchema, mutateCommentSchema } from "@/validators/comments";
interface ThreadProps {
  threadId: string;
  /**
   * First page of the thread, resolved during the server render.
   *
   * Seeding the query is what puts the thread's own title and posts into the HTML.
   * Without it the server sent every thread the same boilerplate intro and nothing else,
   * and Search Console filed 381 of them as "Duplicate without user-selected canonical".
   */
  initialPage: RouterOutputs["comments"]["getForumComments"];
}
export default function Thread(t0) {
  const $ = _c(68);
  const {
    threadId,
    initialPage
  } = t0;
  const limit = FORUM_THREAD_POSTS_PER_PAGE;
  const {
    data: userData
  } = useUserData();
  const [page, setPage] = useState(0);
  const [deletedCommentIds, setDeletedCommentIds] = useState(_temp);
  const [pendingDeletionIds, setPendingDeletionIds] = useState(_temp2);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const pendingDeletionStartedAtRef = useRef(t1);
  const thread_id = threadId;
  const utils = api.useUtils();
  let t2;
  if ($[1] !== page || $[2] !== thread_id) {
    t2 = {
      thread_id,
      limit: FORUM_THREAD_POSTS_PER_PAGE,
      cursor: page
    };
    $[1] = page;
    $[2] = thread_id;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = !!thread_id;
  const t4 = page === 0 ? initialPage : undefined;
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = {
      enabled: t3,
      placeholderData: _temp3,
      initialData: t4
    };
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const {
    data: comments,
    dataUpdatedAt,
    isPlaceholderData,
    isPending: isPendingComments,
    isSuccess: isCommentsSuccess,
    refetch
  } = api.comments.getForumComments.useQuery(t2, t5);
  const thread = comments?.thread;
  const allComments = comments?.data;
  let t6;
  if ($[7] !== allComments || $[8] !== deletedCommentIds) {
    t6 = allComments?.filter(comment => !deletedCommentIds.has(comment.id));
    $[7] = allComments;
    $[8] = deletedCommentIds;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const visibleComments = t6;
  const rawTotalComments = comments?.totalComments ?? 0;
  const pendingDeletionCount = pendingDeletionIds.size;
  const totalComments = Math.max(0, rawTotalComments - pendingDeletionCount);
  const totalPages = Math.ceil(totalComments / FORUM_THREAD_POSTS_PER_PAGE);
  const isSoleCommentOnLastPage = allComments?.length === 1 && comments?.nextCursor === null && totalComments > FORUM_THREAD_POSTS_PER_PAGE;
  const belowForumMinLevel = (userData?.level ?? 0) < FORUM_MIN_LEVEL;
  let t7;
  let t8;
  if ($[10] !== dataUpdatedAt || $[11] !== isCommentsSuccess || $[12] !== isPlaceholderData || $[13] !== pendingDeletionIds.size) {
    t7 = () => {
      if (!isCommentsSuccess || isPlaceholderData || pendingDeletionIds.size === 0) {
        return;
      }
      setPendingDeletionIds(current => {
        const next = new Set(current);
        for (const commentId of current) {
          const startedAt = pendingDeletionStartedAtRef.current.get(commentId);
          if (startedAt !== undefined && dataUpdatedAt >= startedAt) {
            next.delete(commentId);
            pendingDeletionStartedAtRef.current.delete(commentId);
          }
        }
        return next.size === current.size ? current : next;
      });
    };
    t8 = [dataUpdatedAt, isCommentsSuccess, isPlaceholderData, pendingDeletionIds.size];
    $[10] = dataUpdatedAt;
    $[11] = isCommentsSuccess;
    $[12] = isPlaceholderData;
    $[13] = pendingDeletionIds.size;
    $[14] = t7;
    $[15] = t8;
  } else {
    t7 = $[14];
    t8 = $[15];
  }
  useEffect(t7, t8);
  let t9;
  if ($[16] !== dataUpdatedAt || $[17] !== isSoleCommentOnLastPage || $[18] !== page || $[19] !== refetch || $[20] !== thread_id || $[21] !== totalComments || $[22] !== utils.comments.getForumComments) {
    t9 = commentId_0 => {
      const expectedTotal = Math.max(0, totalComments - 1);
      const expectedPages = Math.ceil(expectedTotal / FORUM_THREAD_POSTS_PER_PAGE);
      setDeletedCommentIds(current_0 => {
        if (current_0.has(commentId_0)) {
          return current_0;
        }
        return new Set(current_0).add(commentId_0);
      });
      setPendingDeletionIds(current_1 => {
        if (current_1.has(commentId_0)) {
          return current_1;
        }
        pendingDeletionStartedAtRef.current.set(commentId_0, Math.max(Date.now(), dataUpdatedAt + 1));
        return new Set(current_1).add(commentId_0);
      });
      if (expectedPages > 0 && (page >= expectedPages || isSoleCommentOnLastPage)) {
        setPage(expectedPages - 1);
      }
      utils.comments.getForumComments.setData({
        thread_id,
        limit: FORUM_THREAD_POSTS_PER_PAGE,
        cursor: page
      }, current_2 => {
        if (!current_2?.data.some(comment_0 => comment_0.id === commentId_0)) {
          return current_2;
        }
        return {
          ...current_2,
          data: current_2.data.filter(comment_1 => comment_1.id !== commentId_0)
        };
      });
      refetch({
        throwOnError: true
      }).catch(_temp4);
    };
    $[16] = dataUpdatedAt;
    $[17] = isSoleCommentOnLastPage;
    $[18] = page;
    $[19] = refetch;
    $[20] = thread_id;
    $[21] = totalComments;
    $[22] = utils.comments.getForumComments;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const handleCommentDeleted = t9;
  let t10;
  if ($[24] !== thread_id) {
    t10 = {
      comment: "",
      object_id: thread_id,
      quoteIds: null,
      senderId: null
    };
    $[24] = thread_id;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = zodResolver(mutateCommentSchema);
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10) {
    t12 = {
      defaultValues: t10,
      resolver: t11
    };
    $[27] = t10;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const {
    handleSubmit,
    reset,
    control,
    formState: t13
  } = useForm(t12);
  const {
    errors
  } = t13;
  let t14;
  if ($[29] !== allComments || $[30] !== page || $[31] !== refetch || $[32] !== reset || $[33] !== totalComments || $[34] !== totalPages) {
    t14 = {
      onSuccess: async data => {
        showMutationToast(data);
        if (!data.success) {
          return;
        }
        reset();
        if (totalComments && totalPages && allComments) {
          const newPage = totalComments % FORUM_THREAD_POSTS_PER_PAGE === 0 ? totalPages : totalPages - 1;
          if (newPage !== page) {
            setPage(newPage);
          } else {
            await refetch();
          }
        }
      }
    };
    $[29] = allComments;
    $[30] = page;
    $[31] = refetch;
    $[32] = reset;
    $[33] = totalComments;
    $[34] = totalPages;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const {
    mutate: createComment,
    isPending
  } = api.comments.createForumComment.useMutation(t14);
  let t15;
  if ($[36] !== belowForumMinLevel || $[37] !== createComment) {
    t15 = data_0 => {
      if (belowForumMinLevel) {
        showMutationToast({
          success: false,
          message: forumLevelMessage
        });
        return;
      }
      createComment(data_0);
    };
    $[36] = belowForumMinLevel;
    $[37] = createComment;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== handleSubmit || $[40] !== t15) {
    t16 = handleSubmit(t15, _temp5);
    $[39] = handleSubmit;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  const handleSubmitComment = t16;
  let t17;
  if ($[42] !== thread || $[43] !== userData) {
    t17 = !userData && thread && <ContentBox title="Public Forum" defaultBackHref={`/forum/${thread.boardId}`}>{forumThreadIntro(thread.title)}</ContentBox>;
    $[42] = thread;
    $[43] = userData;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== isPendingComments || $[46] !== thread) {
    t18 = !thread && !isPendingComments && <NotFoundPage />;
    $[45] = isPendingComments;
    $[46] = thread;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== belowForumMinLevel || $[49] !== control || $[50] !== errors || $[51] !== handleCommentDeleted || $[52] !== handleSubmitComment || $[53] !== isPending || $[54] !== page || $[55] !== thread || $[56] !== totalComments || $[57] !== userData || $[58] !== visibleComments) {
    t19 = thread && <ContentBox title="Forum" defaultBackHref={userData ? `/forum/${thread.boardId}` : undefined} initialBreak={!userData} subtitle={thread.title}>{visibleComments?.map((comment_2, i) => <div key={comment_2.id}><CommentOnForum title={i === 0 && page === 0 ? thread.title : undefined} user={comment_2.user} hover_effect={false} comment={comment_2} onDeleted={handleCommentDeleted}>{parseHtml(comment_2.content)}</CommentOnForum></div>)}{thread && userData && !thread.isLocked && !userData.isBanned && !userData.isSilenced && belowForumMinLevel && <p className="mb-3 text-center text-muted-foreground text-sm">{forumLevelMessage}</p>}{thread && userData && !thread.isLocked && !userData.isBanned && !userData.isSilenced && !belowForumMinLevel && <div className="relative mb-3"><RichInput id="comment" height="200" refreshKey={totalComments} placeholder="" control={control} disabled={isPending} error={errors.comment?.message} onSubmit={handleSubmitComment} /><div className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 transform flex-row-reverse">{isPending && <Loader />}</div></div>}</ContentBox>;
    $[48] = belowForumMinLevel;
    $[49] = control;
    $[50] = errors;
    $[51] = handleCommentDeleted;
    $[52] = handleSubmitComment;
    $[53] = isPending;
    $[54] = page;
    $[55] = thread;
    $[56] = totalComments;
    $[57] = userData;
    $[58] = visibleComments;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  let t20;
  if ($[60] !== page || $[61] !== totalPages) {
    t20 = totalPages > 0 && <Pagination current={page} total={totalPages} setPage={setPage} />;
    $[60] = page;
    $[61] = totalPages;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  let t21;
  if ($[63] !== t17 || $[64] !== t18 || $[65] !== t19 || $[66] !== t20) {
    t21 = <>{t17}{t18}{t19}{t20}</>;
    $[63] = t17;
    $[64] = t18;
    $[65] = t19;
    $[66] = t20;
    $[67] = t21;
  } else {
    t21 = $[67];
  }
  return t21;
}
function _temp5(errors_0) {
  return console.error(errors_0);
}
function _temp4() {}
function _temp3(previousData) {
  return previousData;
}
function _temp2() {
  return new Set();
}
function _temp() {
  return new Set();
}